import { AdminPageCabecalhoComponent } from '../../shared/admin-page-cabecalho/admin-page-cabecalho.component';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgForm } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CeiSnackBarService } from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { ListRetrieveUsuario, UsuariosApiService } from '@cei-ufg/ngx-cei-seguranca';
import { PessoaUsuarioApiService, PessoaComUsuario } from '../../services/pessoa-usuario-api.service';
import { PessoaUsuarioFormComponent } from '../../shared/pessoa-usuario-form/pessoa-usuario-form.component';
import { emptyPessoaUsuarioForm } from '../../shared/pessoa-usuario-form/pessoa-usuario-form.model';
import {
  buildPessoaUsuarioPayload,
  carregarUsuarioAutorizacao,
  hasUsuarioData,
  hasUsuarioRelations,
  montarPutUsuarioWithRelations,
  validarContaUsuarioOpcional,
} from '../../shared/pessoa-usuario-form/pessoa-usuario-form.utils';
import { notifyAdminFormError, notifyAdminFormSuccess, stopIfAdminFormInvalid } from '../../shared/admin-form.utils';

@Component({
  selector: 'cei-admin-form',
  standalone: true,
  imports: [AdminPageCabecalhoComponent, CommonModule, RouterModule, PessoaUsuarioFormComponent],
  templateUrl: './form.component.html',
})
export class AdminFormComponent {
  form = emptyPessoaUsuarioForm();
  pendingFoto?: File;

  private readonly api = inject(PessoaUsuarioApiService);
  private readonly usuariosApi = inject(UsuariosApiService);
  private readonly snackBar = inject(CeiSnackBarService);
  private readonly cdr = inject(ChangeDetectorRef);

  onSubmit(form: NgForm) {
    if (stopIfAdminFormInvalid(form)) {
      this.cdr.markForCheck();
      return;
    }

    const erroConta = validarContaUsuarioOpcional(this.form, 'create');
    if (erroConta) {
      this.snackBar.error(erroConta);
      this.cdr.markForCheck();
      return;
    }

    const payload = buildPessoaUsuarioPayload(this.form, hasUsuarioData(this.form));

    this.api
      .createWithUsuario(payload)
      .pipe(
        switchMap((pessoa) =>
          this.salvarPermissoesGruposUsuarioAposCriar(pessoa).pipe(
            catchError((err) => {
              notifyAdminFormError(
                this.snackBar,
                'Pessoa criada, mas falhou ao salvar permissões do usuário.',
                err,
              );
              return of(pessoa);
            }),
          ),
        ),
      )
      .subscribe({
        next: (pessoa) => {
          if (this.pendingFoto && pessoa.id) {
            this.api.uploadFoto(pessoa.id, this.pendingFoto).subscribe({
              next: () => {
                notifyAdminFormSuccess(this.snackBar, 'Pessoa criada com sucesso!');
                this.cdr.markForCheck();
              },
              error: (err) => {
                notifyAdminFormError(this.snackBar, 'Pessoa criada, mas falhou o upload da foto.', err);
                this.cdr.markForCheck();
              },
            });
            return;
          }

          notifyAdminFormSuccess(this.snackBar, 'Pessoa criada com sucesso!');
          this.cdr.markForCheck();
        },
        error: (err) => {
          notifyAdminFormError(this.snackBar, 'Erro ao criar pessoa.', err);
          this.cdr.markForCheck();
        },
      });
  }

  /** Após criar a pessoa, persiste permissões/grupos via UsuariosApiService. */
  private salvarPermissoesGruposUsuarioAposCriar(pessoa: PessoaComUsuario) {
    const login = this.form.login?.trim();
    if (!login || !hasUsuarioRelations(this.form)) {
      return of(pessoa);
    }

    return carregarUsuarioAutorizacao(this.usuariosApi, login).pipe(
      switchMap((usuario: ListRetrieveUsuario | null) => {
        if (!usuario) {
          return of(pessoa);
        }

        return this.usuariosApi
          .usuarioComRelacoesUpdate({
            id: usuario.id,
            putUsuarioWithRelations: montarPutUsuarioWithRelations(this.form, usuario),
          })
          .pipe(map(() => pessoa));
      }),
      catchError(() => of(pessoa)),
    );
  }

  onFotoSelected(file: File): void {
    this.pendingFoto = file;
  }

  onFotoRemoved(): void {
    this.pendingFoto = undefined;
  }
}
